import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { JwtPayload } from '@siteforge/shared';
import * as api from '../services/api';
import { trackSignup } from '../services/analytics';
import { identifyOverseerSignup } from '../services/overseerSync';
import { getClickIds, getMetaCookies, getSignupSource, getTouches, getUtm } from '../services/attribution';

interface AuthContextType {
  user: JwtPayload | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<JwtPayload>;
  register: (email: string, password: string, businessName: string, isFreeTrialSignup?: boolean, phone?: string, tosAccepted?: boolean, honeypot?: { website?: string }) => Promise<{ needsEmailConfirmation?: boolean; email?: string }>;
  logout: () => Promise<void>;
  isAdmin: boolean;
  isStaff: boolean;
  isSalesStaff: boolean;
  isSubAdmin: boolean;
  isVA: boolean;
  isSupport: boolean;
  canAccessAdminConsole: boolean;
  /** #237 — true when this session is an admin "Login as Customer" session
   * (user.impersonatedBy is set). Single source of truth for gating any
   * AUTO-START behavior (page generation, auto-advance, etc.) that must
   * never fire without an explicit click while impersonating — viewing a
   * customer's account must never spend their paid credit on its own.
   * Deliberate, manually-clicked actions are unaffected. */
  isImpersonating: boolean;
  /** #339 — does the signed-in user hold this capability? Effective matrix from GET /auth/me; superadmin always. */
  can: (capability: string) => boolean;
  setTokens: (accessToken: string, refreshToken: string) => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<JwtPayload | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = api.getAccessToken();
    if (token) {
      api.getMe()
        .then((res) => {
          if (res.success && res.data) {
            setUser(res.data);
          } else {
            api.setTokens(null);
          }
        })
        .catch(() => {
          api.setTokens(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<JwtPayload> => {
    const res = await api.login(email, password);
    if (!res.success || !res.data) {
      throw new Error(res.error || 'Login failed');
    }
    api.setTokens({
      accessToken: res.data.accessToken,
      refreshToken: res.data.refreshToken,
    });
    setUser(res.data.user);
    // #339 — the login response carries no capabilities; /auth/me does. Non-blocking, same as setTokens below.
    api.getMe().then((me) => { if (me.success && me.data) setUser(me.data); }).catch(() => { /* keep the login user */ });
    // Clear leftover free-trial signup state so the next /confirm-email
    // landing (e.g. an outreach-claimed user with email_confirmed=false)
    // doesn't display the previous user's email.
    try { sessionStorage.removeItem('pendingConfirmationEmail'); } catch { /* SSR / quota */ }
    return res.data.user;
  }, []);

  const register = useCallback(async (email: string, password: string, businessName: string, isFreeTrialSignup?: boolean, phone?: string, tosAccepted?: boolean, honeypot?: { website?: string }): Promise<{ needsEmailConfirmation?: boolean; email?: string }> => {
    // Resolve attribution (signup source from `?flow=` + click IDs + UTM
    // params captured on first landing) so the backend can persist them on
    // the user row and echo `signupSource` back at email-confirm time. See
    // attribution.ts. Built into a variable (not passed as an inline object
    // literal) so the extra utm_* keys — not part of the frontend
    // `SignupAttribution` type yet — don't trip excess-property checking;
    // they still travel to the backend via JSON.stringify same as gclid etc.
    const resolvedSource = isFreeTrialSignup ? getSignupSource() : 'paid';
    const clickIds = getClickIds();
    const metaCookies = getMetaCookies();
    const utm = getUtm();
    const attribution = {
      signupSource: resolvedSource,
      ...clickIds,
      ...metaCookies,
      ...utm,
      ...getTouches(),
    };
    const res = await api.register(email, password, businessName, isFreeTrialSignup, phone, tosAccepted, attribution, honeypot);
    if (!res.success || !res.data) {
      // Surface the backend's recovery hint (EMAIL_PENDING_CONFIRMATION /
      // EMAIL_EXISTS + the email) so the signup forms can route the user to
      // "check your inbox" or "log in" instead of dead-ending on the banner.
      const e = new Error(res.error || 'Registration failed') as Error & { code?: string; email?: string };
      e.code = (res as any).code;
      e.email = (res as any).email;
      throw e;
    }

    // Free trial: needs email confirmation, don't set tokens
    if (res.data.needsEmailConfirmation) {
      // Free-trial signup: we don't have a user JWT yet, but the backend
      // returned a userId we can thread into GTM.
      const createdUserId = (res.data as any).userId as string | undefined;
      if (createdUserId) {
        trackSignup({ userId: createdUserId, source: resolvedSource, email });
        // m31 O2 — no login happens here, so join this tab's Overseer session to the new customer now.
        identifyOverseerSignup(createdUserId, email);
      }
      return { needsEmailConfirmation: true, email: res.data.email };
    }

    // Paid: auto-login
    api.setTokens({
      accessToken: (res.data as any).accessToken,
      refreshToken: (res.data as any).refreshToken,
    });
    // Fetch user profile after setting tokens
    const meRes = await api.getMe();
    if (meRes.success && meRes.data) {
      setUser(meRes.data);
      trackSignup({ userId: meRes.data.userId, source: resolvedSource, email });
    }
    return {};
  }, []);

  const logoutFn = useCallback(async () => {
    await api.logout();
    setUser(null);
    // Single-source-of-truth redirect: every logout (button, expired-token
    // sweep, hard-fail handler) lands on /login. Hard navigation also clears
    // any in-memory caches that survived setUser(null).
    if (typeof window !== 'undefined') {
      window.location.href = '/login';
    }
  }, []);

  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const isSalesStaff = user?.role === 'sales';
  const isSubAdmin = user?.role === 'sub_admin';
  const isVA = user?.role === 'va';
  // #support-role — a tickets-console staffer. Kept SEPARATE from
  // canAccessAdminConsole (support does NOT get the general admin console —
  // only the Tickets surface, gated explicitly where needed) and OUT of isStaff
  // (support lands on /admin/tickets, not the /sales staff portal).
  const isSupport = user?.role === 'support';
  const isStaff = isSalesStaff || isSubAdmin || isVA;
  // Sub-admins are partial admins with access to the operational console
  // (mirrors the backend requireAdminConsole gate). Centralized here so a new
  // role tier never again silently strands every admin page's mount guard —
  // the pages guard on this, not on isAdmin alone.
  const canAccessAdminConsole = isAdmin || isSubAdmin;
  // #237 — truthy/optional-string check, same idiom ImpersonationBanner.tsx
  // already uses for `user.impersonatedBy` (the impersonating admin's userId).
  const isImpersonating = !!user?.impersonatedBy;
  const can = useCallback(
    (capability: string) => user?.role === 'superadmin' || !!user?.capabilities?.includes(capability),
    [user],
  );

  const setTokensFn = useCallback((accessToken: string, refreshToken: string) => {
    api.setTokens({ accessToken, refreshToken });
    // Refresh user profile
    api.getMe().then((res) => {
      if (res.success && res.data) setUser(res.data);
    });
    // setTokens is called by the outreach-claim auto-login + the
    // SSO-redirect path on CheckoutPage. Clear the free-trial leftover
    // so the EmailConfirmationPage shows the correct user's address
    // when ProtectedRoute redirects an unconfirmed user there.
    try { sessionStorage.removeItem('pendingConfirmationEmail'); } catch { /* SSR / quota */ }
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout: logoutFn, isAdmin, isStaff, isSalesStaff, isSubAdmin, isVA, isSupport, canAccessAdminConsole, isImpersonating, can, setTokens: setTokensFn }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
